import { LoggerService } from '@angular-ru/cdk/logger';
import { ChangeDetectionStrategy, Component, computed, effect, inject, input, signal, untracked } from '@angular/core';
import { IDataboxScore } from 'src/app/api/modules/core/dynamic/databoxes/score/IDataboxScore';

/**
 * The template variable which is replaced by the user's actual score.
 */
const SCORE_TOKEN = '%SCORE%';

/**
 * Displays a live score inside a DataboxContainer.
 *
 * The configured `template` / `templateSingular` strings format the score
 * pushed in through {@link update}; until the first update arrives (or when a
 * new config is applied) the `templateIdle` string is shown. An unconfigured
 * databox renders the built-in defaults.
 *
 * See {@link IDataboxScore} for the configuration structure.
 */
@Component({
  selector: 'app-databox-score',
  templateUrl: './databox-score.component.html',
  styleUrl: './databox-score.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DataboxScoreComponent {
  private readonly loggerService = inject(LoggerService);

  /**
   * The Config as provided to the element.
   *
   * Consumers may bind `null` / `undefined` sources; the built-in defaults
   * are used until a usable config arrives.
   */
  readonly config = input<IDataboxScore | null>();

  /**
   * The last valid score received via {@link update}, or `undefined` while
   * idle (no update yet, or the config changed).
   */
  private readonly score = signal<number | undefined>(undefined);

  /**
   * The validated template strings, falling back to the defaults (and
   * collecting warnings, logged by an effect) when the config is unusable.
   */
  private readonly templates = computed(() => {
    const configuration = this.config();
    const warnings: string[] = [];

    // the template is used to process the numeric values into a formatted score string.
    let template = SCORE_TOKEN;
    if (configuration?.template) {
      if (configuration.template.includes(SCORE_TOKEN)) {
        template = configuration.template;
      } else {
        warnings.push(`The 'template' must contain a substring of "${SCORE_TOKEN}". The default will be used.`);
      }
    }

    // the templateSingular fixes grammar when the score is exactly 1.
    let templateSingular = SCORE_TOKEN;
    if (configuration?.templateSingular) {
      if (configuration.templateSingular.includes(SCORE_TOKEN)) {
        templateSingular = configuration.templateSingular;
      } else {
        warnings.push(`The 'templateSingular' must contain a substring of "${SCORE_TOKEN}". The default will be used.`);
      }
    }

    // template idle is shown until an acceptable value has been provided.
    const templateIdle = configuration?.templateIdle || '-';

    return { template, templateSingular, templateIdle, warnings };
  });

  /**
   * The formatted string the template displays.
   */
  protected readonly output = computed(() => {
    const { template, templateSingular, templateIdle } = this.templates();
    const score = this.score();

    if (score === undefined) {
      return templateIdle;
    }

    return (score === 1 ? templateSingular : template).replace(SCORE_TOKEN, score.toString());
  });

  constructor() {
    // surface configuration problems (once per config change)
    effect(() => {
      for (const warning of this.templates().warnings) {
        this.loggerService.warn(`[DataboxScore] - ${warning}`);
      }
    });

    // a new config returns the databox to its idle output until the next update
    effect(() => {
      this.config();
      untracked(() => this.score.set(undefined));
    });
  }

  /**
   * Updates the component with the user's score.
   *
   * A non-numeric value is ignored and the current output is kept.
   *
   * @param score - the score of the user
   */
  update(score?: number) {
    if (typeof score !== 'number') {
      return;
    }
    this.score.set(score);
  }
}
